import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  ArrowRight,
  Car,
  MapPin,
  Calendar as CalendarIcon,
  Check,
  Star,
  Shield,
  Fuel,
  Users,
  AlertCircle
} from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import api from '../lib/api';
import { fetchPartnerServicesEnabled } from '../lib/partnerServicesAvailability';
import PartnerServicesComingSoon from '../components/PartnerServicesComingSoon';

const PICKUP_LOCATIONS = [
  { id: 'frankfurt', name: 'Frankfurt Airport (FRA)', popular: true },
  { id: 'munich', name: 'Munich Airport (MUC)', popular: true },
  { id: 'ramstein', name: 'Ramstein Air Base', popular: true },
  { id: 'stuttgart', name: 'Stuttgart Airport (STR)', popular: false },
  { id: 'berlin', name: 'Berlin Airport (BER)', popular: false },
  { id: 'kaiserslautern', name: 'Kaiserslautern City', popular: false },
  { id: 'wiesbaden', name: 'Wiesbaden City', popular: false },
];

export default function RentalCarsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [featureEnabled, setFeatureEnabled] = useState(false);
  
  // Form state
  const [pickupLocation, setPickupLocation] = useState('');
  const [returnLocation, setReturnLocation] = useState('');
  const [sameReturn, setSameReturn] = useState(true);
  const [pickupDate, setPickupDate] = useState(null);
  const [returnDate, setReturnDate] = useState(null);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [quote, setQuote] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const enabled = await fetchPartnerServicesEnabled();
        setFeatureEnabled(enabled);

        if (enabled) {
          const packagesRes = await api.get('/rental-cars/packages');
          const pkgData = packagesRes.data.packages;
          const pkgArray = Array.isArray(pkgData)
            ? pkgData
            : Object.entries(pkgData).map(([id, pkg]) => ({
                id,
                ...pkg,
                price_per_day: pkg.daily_rate || pkg.price_per_day,
              }));
          setPackages(pkgArray);
        }
      } catch (error) {
        console.error('Error fetching rental car data:', error);
        if (error.response?.status === 503) {
          setFeatureEnabled(false);
        }
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleGetQuote = async () => {
    if (!pickupLocation || !pickupDate || !returnDate || !selectedPackage) return;
    
    setQuoteLoading(true);
    try {
      const res = await api.post('/rental-cars/quote', null, {
        params: {
          package_id: selectedPackage.id,
          pickup_date: pickupDate.toISOString(),
          return_date: returnDate.toISOString(),
          pickup_location: pickupLocation,
          return_location: sameReturn ? pickupLocation : returnLocation
        }
      });
      setQuote(res.data);
    } catch (error) {
      console.error('Quote error:', error);
    } finally {
      setQuoteLoading(false);
    }
  };

  const handleBook = async () => {
    if (!user) {
      navigate('/login?redirect=/rental-cars');
      return;
    }
    
    try {
      const res = await api.post('/rental-cars/book', null, {
        params: {
          package_id: selectedPackage.id,
          pickup_date: pickupDate.toISOString(),
          return_date: returnDate.toISOString(),
          pickup_location: pickupLocation,
          return_location: sameReturn ? pickupLocation : returnLocation
        }
      });
      toast.success(
        `${t('rental.booking_confirmed', 'Booking confirmed!')} ID: ${res.data.booking_id}`,
      );
      navigate('/settings');
    } catch (error) {
      console.error('Booking error:', error);
      toast.error(t('rental.booking_failed', 'Booking failed. Please try again.'));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 pt-20 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-signal-blue border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!featureEnabled) {
    return <PartnerServicesComingSoon />;
  }

  return (
    <div className="min-h-screen bg-slate-100 pt-20" data-testid="rental-cars-page">
      {/* Hero Section */}
      <div className="bg-gradient-to-b from-slate-900 to-slate-950 py-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-military-gold/10 border border-military-gold/30 rounded-full mb-4">
              <Car className="w-4 h-4 text-military-gold" />
              <span className="text-military-gold text-sm">TLA Partner Service</span>
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl font-bold text-slate-800 mb-4">
              RENTAL CARS
            </h1>
            <p className="text-slate-600 text-lg max-w-2xl mx-auto">
              Explore Germany with ease. Special rates for military families with flexible pickup at airports and bases.
            </p>
          </div>

          {/* Booking Form */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              {/* Pickup Location */}
              <div>
                <label className="block text-sm text-slate-600 mb-2">Pickup Location</label>
                <Select value={pickupLocation} onValueChange={setPickupLocation}>
                  <SelectTrigger className="bg-white border-slate-300 text-slate-800">
                    <SelectValue placeholder="Select location" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-slate-300">
                    {PICKUP_LOCATIONS.map(loc => (
                      <SelectItem key={loc.id} value={loc.id} className="text-slate-800">
                        {loc.name} {loc.popular && '⭐'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Return Location */}
              <div>
                <label className="block text-sm text-slate-600 mb-2">Return Location</label>
                <div className="flex items-center gap-2 mb-2">
                  <input
                    type="checkbox"
                    checked={sameReturn}
                    onChange={(e) => setSameReturn(e.target.checked)}
                    className="rounded border-slate-300 bg-white"
                  />
                  <span className="text-sm text-slate-600">{t('rental.same_as_pickup', 'Same as pickup')}</span>
                </div>
                {!sameReturn && (
                  <Select value={returnLocation} onValueChange={setReturnLocation}>
                    <SelectTrigger className="bg-white border-slate-300 text-slate-800">
                      <SelectValue placeholder="Select location" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-slate-300">
                      {PICKUP_LOCATIONS.map(loc => (
                        <SelectItem key={loc.id} value={loc.id} className="text-slate-800">
                          {loc.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>

              {/* Pickup Date */}
              <div>
                <label className="block text-sm text-slate-600 mb-2">Pickup Date</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full bg-white border-slate-300 text-slate-800 justify-start">
                      <CalendarIcon className="w-4 h-4 mr-2" />
                      {pickupDate ? format(pickupDate, 'MMM d, yyyy') : 'Select date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-white border-slate-300">
                    <Calendar
                      mode="single"
                      selected={pickupDate}
                      onSelect={setPickupDate}
                      disabled={(date) => date < new Date()}
                    />
                  </PopoverContent>
                </Popover>
              </div>

              {/* Return Date */}
              <div>
                <label className="block text-sm text-slate-600 mb-2">Return Date</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full bg-white border-slate-300 text-slate-800 justify-start">
                      <CalendarIcon className="w-4 h-4 mr-2" />
                      {returnDate ? format(returnDate, 'MMM d, yyyy') : 'Select date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-white border-slate-300">
                    <Calendar
                      mode="single"
                      selected={returnDate}
                      onSelect={setReturnDate}
                      disabled={(date) => date < (pickupDate || new Date())}
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Vehicle Packages */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        <h2 className="font-heading text-2xl font-bold text-slate-800 mb-8 text-center">
          Choose Your Vehicle
        </h2>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {packages.map((pkg) => (
            <div
              key={pkg.id}
              onClick={() => setSelectedPackage(pkg)}
              className={`bg-white rounded-xl shadow-sm border border-slate-200 p-6 cursor-pointer transition-all ${
                selectedPackage?.id === pkg.id 
                  ? 'ring-2 ring-signal-blue' 
                  : 'hover:bg-slate-50'
              }`}
              data-testid={`package-${pkg.id}`}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading text-xl font-bold text-slate-800">{pkg.name}</h3>
                {selectedPackage?.id === pkg.id && (
                  <Check className="w-5 h-5 text-signal-blue" />
                )}
              </div>
              
              <p className="text-slate-600 text-sm mb-4">{pkg.description}</p>
              
              <div className="text-slate-800 text-sm mb-4">
                Examples: {pkg.examples.join(', ')}
              </div>
              
              <div className="flex flex-wrap gap-2 mb-4">
                {pkg.features.slice(0, 4).map((feature, idx) => (
                  <span 
                    key={idx}
                    className="text-xs px-2 py-1 bg-slate-200 text-slate-700 rounded"
                  >
                    {feature}
                  </span>
                ))}
              </div>
              
              <div className="flex items-baseline justify-between pt-4 border-t border-slate-300">
                <span className="text-2xl font-bold text-military-gold">€{pkg.price_per_day}</span>
                <span className="text-slate-800 text-sm">/day</span>
              </div>
            </div>
          ))}
        </div>

        {/* Quote Section */}
        {selectedPackage && pickupLocation && pickupDate && returnDate && (
          <div className="mt-8 max-w-2xl mx-auto">
            <Button
              onClick={handleGetQuote}
              disabled={quoteLoading}
              className="w-full bg-signal-blue hover:bg-signal-blue-hover mb-4"
              data-testid="get-quote-btn"
            >
              {quoteLoading ? 'Calculating...' : 'Get Quote'}
            </Button>
            
            {quote && (
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6" data-testid="quote-result">
                <h3 className="font-heading text-lg font-bold text-slate-800 mb-4">Your Quote</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between text-slate-600">
                    <span>Vehicle ({quote.package.name})</span>
                    <span>€{quote.package.price_per_day}/day x {quote.days} days</span>
                  </div>
                  <div className="flex justify-between text-slate-600">
                    <span>Base price</span>
                    <span>€{quote.base_price}</span>
                  </div>
                  {quote.different_location_fee > 0 && (
                    <div className="flex justify-between text-slate-600">
                      <span>{t('rental.different_return', 'Different return location')}</span>
                      <span>€{quote.different_location_fee}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-slate-600">
                    <span>Insurance</span>
                    <span>€{quote.insurance_fee}</span>
                  </div>
                  <div className="flex justify-between text-slate-800 font-bold text-lg pt-2 border-t border-slate-300">
                    <span>Total</span>
                    <span>€{quote.total}</span>
                  </div>
                </div>
                
                <Button
                  onClick={handleBook}
                  className="w-full mt-6 bg-military-gold hover:bg-military-gold/90 text-slate-900"
                  data-testid="book-now-btn"
                >
                  {user ? 'Book Now' : 'Sign In to Book'}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Benefits Section */}
      <div className="bg-white/50 py-12">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="font-heading text-2xl font-bold text-slate-800 mb-8 text-center">
            Why Rent With Us
          </h2>
          <div className="grid md:grid-cols-4 gap-6">
            {[
              { icon: Shield, title: 'Full Insurance', desc: 'Comprehensive coverage included' },
              { icon: MapPin, title: 'Base Pickup', desc: 'Convenient military base locations' },
              { icon: Fuel, title: 'Full Tank', desc: 'Start with a full tank policy' },
              { icon: Users, title: 'Family Friendly', desc: 'Child seats available free' },
            ].map((benefit, idx) => (
              <div key={idx} className="text-center">
                <div className="w-12 h-12 bg-signal-blue/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <benefit.icon className="w-6 h-6 text-signal-blue" />
                </div>
                <h3 className="font-medium text-slate-800 mb-2">{benefit.title}</h3>
                <p className="text-slate-600 text-sm">{benefit.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
